import { AppContextProvider } from '@tock/money';
import { AsyncRenderProvider } from 'contexts/asyncRender';
import { CssBaseline } from '@mui/material';
import { DndProvider, TouchTransition } from 'react-dnd-multi-backend';
import { FeatureFlagContext } from '@tock/shared/utils/featureFlagContext';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntlProvider } from 'react-intl';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { StoreType } from 'utils/connect';
import { ThemeProvider } from '@mui/material/styles';
import { TouchBackend } from 'react-dnd-touch-backend';
import { defaultMemoize } from 'reselect';
import { getTheme } from 'theme/Theme';
import { useSelector } from 'react-redux';
import LoginRoute from 'routes/LoginRoute';
import ProtectedRoute from 'routes/ProtectedRoute';
import RoleContextProvider from 'components/product/RoleContextProvider';
import SelectRoute from 'routes/SelectRoute';
import Toast from 'components/Toast';

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      options: { enableMouseEvents: true, delay: 1, touchSlop: 12 },
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true, delay: 1, touchSlop: 12 },
      preview: false,
      transition: TouchTransition,
    },
  ],
};

const getAppTheme = defaultMemoize((type: 'light' | 'dark') => getTheme(type));

const Application: React.FC<React.PropsWithChildren<unknown>> = () => {
  const currency = useSelector((state: StoreType) => state.app.currency);
  const features = useSelector((state: StoreType) => state.features.globalFeatures);
  const timeZone = useSelector((state: StoreType) => state.app.timeZone);
  const isAuthenticated = !!useSelector((state: StoreType) => state.app.jwtToken);

  const theme = getAppTheme('light');

  return (
    <ThemeProvider theme={theme}>
      <DndProvider options={HTML5toTouch}>
        <AppContextProvider
          currency={currency}
          locale={'en-us'}
          features={features}
          timeZone={timeZone}
        >
          <FeatureFlagContext.Provider value={features}>
            <RoleContextProvider>
              <AsyncRenderProvider>
                <IntlProvider locale={'en-us'}>
                  <Router>
                    <CssBaseline />
                    <div className="Wrapper">
                      <Switch>
                        <Route component={LoginRoute} path="/login" />

                        <ProtectedRoute
                          component={SelectRoute}
                          protection={{
                            requireThat: isAuthenticated,
                            elseRedirectTo: '/login',
                          }}
                          path="/"
                        />
                      </Switch>

                      <Toast />
                    </div>
                  </Router>
                </IntlProvider>
              </AsyncRenderProvider>
            </RoleContextProvider>
          </FeatureFlagContext.Provider>
        </AppContextProvider>
      </DndProvider>
    </ThemeProvider>
  );
};

export default Application;
